/**
 * Centro de Documentación AlfaNet
 * ────────────────────────────────────────────────────────────
 * Punto único de acceso a la documentación del sistema:
 *  1. Guía de Configuración de Roles (M17)
 *  2. Manual de Inteligencia de Agentes (A1-A6)
 *  3. Tutoriales de Operación (paso a paso operativo)
 *
 * Reemplaza los accesos directos individuales del launcher.
 * Accesible desde el ícono ? del Global Header.
 */
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { GuiaRolesContent } from "./GuiaRoles";
import { ManualInteligenciaContent } from "./ManualInteligencia";
import { ManualTiposSolucionContent } from "./ManualTiposSolucion";
import { ManualesUsuarioContent } from "./ManualesUsuario";   // v96.5.0
import {
  Shield,
  BrainCircuit,
  GraduationCap,
  PlayCircle,
  ClipboardList,
  MapPinned,
  BookOpen,
  Bot,
  FileText,
  ChevronRight,
  TreePine,
  Workflow,
  RefreshCw,
  ShieldCheck,
  SortDesc,
  CalendarClock,
  Camera,
  Smartphone,
  Loader2,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import api from "../lib/api";

const TABS = [
  {
    key: "manuales",
    label: "Manuales de Usuario",
    short: "Manuales",
    icon: BookOpen,
    hash: "#manuales",
  },
  {
    key: "pipeline",
    label: "Pipeline de Calidad",
    short: "Pipeline",
    icon: Workflow,
    hash: "#pipeline",
  },
  {
    key: "tipos-solucion",
    label: "Manual de Tipos de Solución",
    short: "Tipos",
    icon: TreePine,
    hash: "#tipos-solucion",
  },
  {
    key: "roles",
    label: "Guía de Configuración de Roles",
    short: "Roles",
    icon: Shield,
    hash: "#roles",
  },
  {
    key: "inteligencia",
    label: "Manual de Inteligencia de Agentes",
    short: "Agentes",
    icon: BrainCircuit,
    hash: "#inteligencia",
  },
  {
    key: "tutoriales",
    label: "Tutoriales de Operación",
    short: "Tutoriales",
    icon: GraduationCap,
    hash: "#tutoriales",
  },
];

export default function CentroDocumentacion() {
  const location = useLocation();
  const navigate = useNavigate();
  // v99.5.3 · Support nested hashes like #tipos-solucion#5
  const rawHash = location.hash || "";
  const mainHash = rawHash.split("#").filter(Boolean)[0] || "";
  const initial = TABS.find((t) => t.hash === `#${mainHash}`)?.key || "pipeline";
  const [active, setActive] = useState(initial);

  useEffect(() => {
    const parts = (location.hash || "").split("#").filter(Boolean);
    const tabHash = parts[0] || "";
    const t = TABS.find((x) => x.hash === `#${tabHash}`);
    if (t) setActive(t.key);
  }, [location.hash]);

  const setTab = (key) => {
    setActive(key);
    const t = TABS.find((x) => x.key === key);
    if (t) navigate(`/centro-documentacion${t.hash}`, { replace: true });
  };

  return (
    <Layout title="Centro de Documentación AlfaNet" subtitle="Manuales · Guías · Tutoriales">
      <div className="max-w-6xl mx-auto pb-16" data-testid="centro-documentacion-page">
        {/* Tabs sticky */}
        <div
          className="sticky top-0 z-20 -mx-4 md:mx-0 mb-6 bg-white/95 backdrop-blur border-b border-gray-200"
          data-testid="centro-doc-tabs"
        >
          <div className="flex overflow-x-auto no-scrollbar">
            {TABS.map((t) => {
              const Icon = t.icon;
              const isActive = active === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`flex items-center gap-2 px-5 py-3 border-b-2 transition whitespace-nowrap text-sm font-semibold ${
                    isActive
                      ? "border-[#0A3747] text-[#0A3747]"
                      : "border-transparent text-gray-500 hover:text-[#0A3747] hover:bg-gray-50"
                  }`}
                >
                  <Icon size={16} className={isActive ? "text-[#E0E621]" : ""} />
                  <span className="hidden md:inline">{t.label}</span>
                  <span className="md:hidden">{t.short}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Contenido */}
        {active === "pipeline" && (
          <div data-testid="pane-pipeline">
            <PipelineCalidad />
          </div>
        )}

        {active === "manuales" && (
          <div data-testid="pane-manuales">
            <ManualesUsuarioContent />
          </div>
        )}

        {active === "tipos-solucion" && (
          <div data-testid="pane-tipos-solucion">
            <ManualTiposSolucionContent />
          </div>
        )}

        {active === "roles" && (
          <div data-testid="pane-roles">
            <GuiaRolesContent />
          </div>
        )}

        {active === "inteligencia" && (
          <div data-testid="pane-inteligencia">
            <ManualInteligenciaContent />
          </div>
        )}

        {active === "tutoriales" && (
          <div data-testid="pane-tutoriales">
            <TutorialesOperacion />
          </div>
        )}
      </div>
    </Layout>
  );
}

// ═══════════════════════════════════════════════════════════
// Pipeline de Calidad · v6.3.1 · Documentación Operativa
// ═══════════════════════════════════════════════════════════

const PIPELINE_STEPS = [
  {
    key: "sync",
    label: "SYNC",
    subtitle: "Odoo → AlfaField",
    icon: RefreshCw,
    color: "#2563EB",
    colorBg: "#EFF6FF",
    colorBorder: "#BFDBFE",
    statsKey: "sync",
    statsLabel: "Esperando",
    rules: [
      "Fecha de creación ≥ 2024",
      "Stage (etapa) no nula en Odoo",
      "Polling cada 120 segundos",
      "JOIN con service_activity_type para poblar activity_type_code",
      "Filtro de stages excluidos: Cancelled, Anulado, Rechazado",
    ],
  },
  {
    key: "a1",
    label: "A1",
    subtitle: "Agente Evaluador",
    icon: ShieldCheck,
    color: "#059669",
    colorBg: "#ECFDF5",
    colorBorder: "#A7F3D0",
    statsKey: "review",
    statsLabel: "En Revisión",
    rules: [
      "activity_type_code obligatorio — vacío = REVIEW",
      "Match exacto en solution_tree_nodes.odoo_activity_type",
      "solution_type_id obligatorio — nulo = REVIEW (Candado Total v7.5.1)",
      "Datos obligatorios: nombre, móvil, coordenadas GPS",
      "Estado de suscripción válido (whitelist configurable)",
      "Bypass infraestructura/radio: exime SLA, pero exige nodo mapeado",
    ],
  },
  {
    key: "a6",
    label: "A6",
    subtitle: "Priorizador",
    icon: SortDesc,
    color: "#7C3AED",
    colorBg: "#F5F3FF",
    colorBorder: "#DDD6FE",
    statsKey: "a6_queue",
    statsLabel: "En Cola",
    rules: [
      "Score compuesto: SLA urgencia + antigüedad + segmento",
      "Recompute automático cada 5 minutos",
      "Lock de OT para evitar doble asignación",
      "Prioridad P1 > P2 > P3 con peso configurable",
    ],
  },
  {
    key: "a2",
    label: "A2",
    subtitle: "Auto-Agendador",
    icon: CalendarClock,
    color: "#D97706",
    colorBg: "#FFFBEB",
    colorBorder: "#FDE68A",
    statsKey: "a1_approved",
    statsLabel: "Aprobadas",
    rules: [
      "Horario laboral: L-V 08:00 – 18:00",
      "No-overlap estricto entre citas del mismo técnico",
      "Buffer de 30 minutos entre asignaciones",
      "Respeta zona geográfica del técnico",
      "Genera slot automático en el primer hueco disponible",
    ],
  },
  {
    key: "a3",
    label: "A3",
    subtitle: "Fiscalización IA",
    icon: Camera,
    color: "#DC2626",
    colorBg: "#FEF2F2",
    colorBorder: "#FECACA",
    statsKey: "assigned",
    statsLabel: "Asignadas",
    rules: [
      "Geo-fence: técnico debe estar a ≤ 500m del cliente",
      "Cero fotos de galería — solo captura en vivo (EXIF obligatorio)",
      "29 fotos obligatorias por checklist de instalación",
      "Validación de rostro/equipo por IA (Nano Banana)",
      "Timestamp y coordenadas embebidos en cada foto",
    ],
  },
  {
    key: "done",
    label: "APP",
    subtitle: "Cierre Móvil",
    icon: Smartphone,
    color: "#0A3747",
    colorBg: "#F0F9FF",
    colorBorder: "#BAE6FD",
    statsKey: "completed",
    statsLabel: "Completadas",
    rules: [
      "Firma digital del cliente en pantalla",
      "Acta de servicio generada automáticamente",
      "Sync de fotos y datos al backend",
      "Notificación al supervisor al cerrar",
    ],
  },
];

function PipelineCalidad() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/v1/ot_smart/pipeline-stats");
        if (!cancel) setStats(data);
      } catch (e) {
        console.error("Pipeline stats error:", e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const getCount = (key) => stats?.[key] ?? "—";

  return (
    <div className="space-y-8" data-testid="pipeline-calidad">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto">
        <h2 className="text-2xl font-bold text-[#0A3747] mb-2">Pipeline de Calidad</h2>
        <p className="text-sm text-gray-500">
          Cada orden de trabajo pasa por 6 etapas de validación antes de llegar al técnico.
          Si alguna regla no se cumple, la OT queda bloqueada en revisión hasta que se corrija.
        </p>
        {stats && (
          <div className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 bg-[#0A3747] text-white rounded-full text-xs font-mono" data-testid="pipeline-total">
            <span className="font-bold text-[#E0E621]">{stats.total}</span> OTs en el sistema
          </div>
        )}
      </div>

      {/* ── Flow Line ── */}
      <div className="relative" data-testid="pipeline-flow">
        {/* Connection line */}
        <div className="hidden lg:block absolute top-[72px] left-[8%] right-[8%] h-0.5 bg-gradient-to-r from-blue-300 via-emerald-300 via-amber-300 to-slate-300 z-0" />

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {PIPELINE_STEPS.map((step, i) => {
            const Icon = step.icon;
            const count = getCount(step.statsKey);
            const isExpanded = expanded === step.key;
            return (
              <div key={step.key} className="flex flex-col items-center relative z-10">
                {/* Bubble */}
                <button
                  onClick={() => setExpanded(isExpanded ? null : step.key)}
                  className="group relative flex flex-col items-center cursor-pointer transition-transform hover:scale-105"
                  data-testid={`pipeline-step-${step.key}`}
                >
                  <div
                    className="w-20 h-20 rounded-3xl flex items-center justify-center shadow-lg transition-shadow group-hover:shadow-xl"
                    style={{ background: step.colorBg, border: `2px solid ${step.colorBorder}` }}
                  >
                    <Icon size={28} style={{ color: step.color }} strokeWidth={1.8} />
                  </div>
                  {/* Count badge */}
                  <div
                    className="absolute -top-1 -right-1 min-w-[28px] h-7 flex items-center justify-center rounded-full text-white text-[11px] font-bold px-1.5 shadow-md"
                    style={{ background: step.color }}
                    data-testid={`pipeline-count-${step.key}`}
                  >
                    {loading ? <Loader2 size={12} className="animate-spin" /> : count}
                  </div>
                  {/* Label */}
                  <div className="mt-2.5 text-center">
                    <div className="text-xs font-black tracking-wide" style={{ color: step.color }}>{step.label}</div>
                    <div className="text-[10px] text-gray-500">{step.subtitle}</div>
                  </div>
                  {/* Stats label */}
                  <div className="text-[10px] font-mono mt-0.5" style={{ color: step.color }}>
                    {step.statsLabel}
                  </div>
                </button>

                {/* Arrow connector (visible on lg only, between steps) */}
                {i < PIPELINE_STEPS.length - 1 && (
                  <ArrowRight size={16} className="hidden lg:block absolute -right-4 top-[36px] text-gray-300" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Expanded Rules Panel ── */}
      {expanded && (() => {
        const step = PIPELINE_STEPS.find((s) => s.key === expanded);
        if (!step) return null;
        const Icon = step.icon;
        return (
          <div
            className="rounded-3xl p-6 mx-auto max-w-3xl animate-in slide-in-from-top-2"
            style={{ background: step.colorBg, border: `2px solid ${step.colorBorder}`, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}
            data-testid={`pipeline-rules-${step.key}`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: step.color }}>
                <Icon size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold" style={{ color: step.color }}>
                  {step.label} · {step.subtitle}
                </h3>
                <p className="text-[11px] text-gray-500">Reglas de hierro de esta etapa</p>
              </div>
            </div>
            <ul className="space-y-2">
              {step.rules.map((rule, j) => (
                <li key={j} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 size={15} style={{ color: step.color, flexShrink: 0, marginTop: 2 }} />
                  <span className="text-gray-700">{rule}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })()}

      {/* ── Full Rules Table ── */}
      <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm" data-testid="pipeline-rules-table">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-bold text-[#0A3747]">Reglas y Restricciones por Etapa</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">Haz clic en las burbujas para ver detalles. Aquí tienes el resumen completo.</p>
        </div>
        <div className="divide-y divide-gray-100">
          {PIPELINE_STEPS.map((step) => {
            const Icon = step.icon;
            return (
              <div key={step.key} className="px-5 py-3 flex gap-4 items-start hover:bg-gray-50/50 transition">
                <div
                  className="w-9 h-9 rounded-2xl flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: step.colorBg, border: `1.5px solid ${step.colorBorder}` }}
                >
                  <Icon size={16} style={{ color: step.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold" style={{ color: step.color }}>{step.label}</span>
                    <span className="text-[10px] text-gray-400">{step.subtitle}</span>
                  </div>
                  <ul className="space-y-0.5">
                    {step.rules.map((rule, j) => (
                      <li key={j} className="text-[11px] text-gray-600 flex items-start gap-1.5">
                        <span style={{ color: step.color }} className="mt-0.5">•</span>
                        {rule}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex-shrink-0 text-right">
                  <div className="text-lg font-bold tabular-nums" style={{ color: step.color }}>
                    {loading ? "—" : getCount(step.statsKey)}
                  </div>
                  <div className="text-[9px] text-gray-400 uppercase">{step.statsLabel}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="text-center text-[10px] text-gray-400 py-4">
        Pipeline de Calidad AlfaField Connect v6.3.1 · Los conteos se actualizan en tiempo real desde la base de datos.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// Tutoriales de Operación · sección nueva
// ═══════════════════════════════════════════════════════════
const TUTORIALES = [
  {
    id: "crear-ot",
    icon: ClipboardList,
    color: "#2563EB",
    title: "Crear una Orden de Trabajo manualmente",
    audience: "Supervisor · Coordinador",
    duration: "3 min",
    steps: [
      "Abre el módulo Órdenes de Trabajo desde el Dashboard.",
      "Click en «Nueva Orden» (arriba a la derecha).",
      "Completa Cliente, Dirección y Tipo de Solución.",
      "Verifica que el semáforo de stock esté en verde antes de guardar.",
      "Asigna técnico manualmente o deja que A2 lo agende automáticamente.",
    ],
  },
  {
    id: "asignar-tecnico",
    icon: MapPinned,
    color: "#0D9488",
    title: "Asignar técnico a una OT desde el mapa",
    audience: "Supervisor",
    duration: "2 min",
    steps: [
      "Ve al módulo Mapa GPS del día.",
      "Selecciona la OT pendiente (chip rojo en el mapa).",
      "Click en el técnico más cercano (chip azul).",
      "Confirma la asignación en el diálogo emergente.",
      "El técnico recibirá una notificación WhatsApp automática.",
    ],
  },
  {
    id: "revisar-agente",
    icon: Bot,
    color: "#7C3AED",
    title: "Revisar decisiones del Agente A2 (Agendamiento)",
    audience: "Supervisor · Administrador",
    duration: "4 min",
    steps: [
      "Abre Inteligencia y Agentes → Auditoría A2.",
      "Filtra por rango de fechas y estado (Propuesta · Aplicada · Rechazada).",
      "Click en una fila para ver el razonamiento del agente.",
      "Puedes aceptar, rechazar o modificar la propuesta desde ahí.",
    ],
  },
  {
    id: "fiscalizar",
    icon: FileText,
    color: "#D97706",
    title: "Fiscalizar una instalación en campo",
    audience: "Técnico",
    duration: "5 min",
    steps: [
      "Al finalizar la instalación, abre la OT desde tu móvil.",
      "Click en «Fiscalizar» (verde).",
      "Toma las fotos obligatorias según el tipo de solución.",
      "Completa los campos dinámicos del formulario.",
      "Firma con el cliente y envía. La OT pasará automáticamente a «Cerrada».",
    ],
  },
];

function TutorialesOperacion() {
  return (
    <div className="space-y-6" data-testid="tutoriales-operacion">
      {/* Hero */}
      <div
        className="rounded-2xl p-6 md:p-8 border border-[#E0E621]/30 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0A3747 0%, #135166 100%)" }}
        data-testid="tutoriales-hero"
      >
        <div className="absolute right-6 top-6 opacity-10">
          <GraduationCap size={180} className="text-[#E0E621]" />
        </div>
        <div className="relative z-10">
          <div className="text-[10px] uppercase tracking-[0.3em] font-mono text-[#E0E621] mb-2">
            Tutoriales · Paso a paso
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: "Chivo" }}>
            Aprende operando
          </h2>
          <p className="text-white/80 max-w-2xl text-sm md:text-base">
            Recorridos cortos y prácticos para las tareas más comunes del sistema. Cada tutorial detalla
            los pasos exactos y el rol destinatario.
          </p>
        </div>
      </div>

      {/* Lista de tutoriales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {TUTORIALES.map((t) => {
          const Icon = t.icon;
          return (
            <details
              key={t.id}
              className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-[#0A3747]/30 transition"
              data-testid={`tutorial-${t.id}`}
            >
              <summary className="flex items-start gap-4 p-5 cursor-pointer list-none">
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: t.color + "18", color: t.color }}
                >
                  <Icon size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-gray-400">
                      {t.audience}
                    </span>
                    <span className="text-[10px] font-mono text-gray-400">·</span>
                    <span className="text-[10px] font-mono text-gray-400 flex items-center gap-1">
                      <PlayCircle size={10} /> {t.duration}
                    </span>
                  </div>
                  <div className="font-semibold text-[#0A3747] text-sm md:text-base leading-snug">
                    {t.title}
                  </div>
                </div>
                <ChevronRight
                  size={18}
                  className="text-gray-400 group-open:rotate-90 transition-transform mt-2 flex-shrink-0"
                />
              </summary>
              <div className="px-5 pb-5 pt-1 border-t border-gray-100">
                <ol className="space-y-2 mt-3">
                  {t.steps.map((s, i) => (
                    <li key={i} className="flex gap-3 text-[13px] text-gray-700">
                      <span
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold text-white"
                        style={{ background: t.color }}
                      >
                        {i + 1}
                      </span>
                      <span className="flex-1 pt-0.5 leading-relaxed">{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </details>
          );
        })}
      </div>

      <div className="text-center text-[11px] text-gray-400 pt-2 pb-4" data-testid="tutoriales-footer">
        ¿Necesitas un tutorial específico? Contacta al administrador para solicitarlo.
      </div>
    </div>
  );
}
